import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { campaignAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import {
    DocumentTextIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    UserGroupIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SpeakerWaveIcon,
    ChartBarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    CurrencyRupeeIcon,
    LightBulbIcon,
    ShieldCheckIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    CalendarDaysIcon,
    BoltIcon,
    SignalIcon,
    ChartPieIcon,
    CreditCardIcon,
    SparklesIcon,
    ServerIcon,
    CpuChipIcon,
    UsersIcon,
    GlobeAltIcon,
    AcademicCapIcon,
    TrophyIcon,
    EyeIcon,
    ClipboardDocumentListIcon,
    ExclamationCircleIcon as WarningIcon,
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, isAdmin } = useAuth();
    const [selectedUser, setSelectedUser] = useState('all');
    const [selectedAdminCampaign, setSelectedAdminCampaign] = useState('all');
    const [adminVoiceStats, setAdminVoiceStats] = useState(null);

    useEffect(() => {
        if (isAdmin()) {
            fetchAdminDashboardStats();
        }
    }, []);

    useEffect(() => {
        if (isAdmin() && stats) {
            fetchAdminVoiceStats();
        }
    }, [selectedUser, selectedAdminCampaign]);

    // Set initial voice stats when stats are first loaded
    useEffect(() => {
        if (stats && selectedUser === 'all' && selectedAdminCampaign === 'all') {
            setAdminVoiceStats(stats.systemListeningStatistics || null);
        }
    }, [stats]);

    // Reset campaign filter when user changes
    useEffect(() => {
        setSelectedAdminCampaign('all');
    }, [selectedUser]);

    const fetchAdminDashboardStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await campaignAPI.getAdminDashboardStats();
            setStats(response.data.data);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to load admin dashboard statistics';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAdminVoiceStats = useCallback(async () => {
        if (selectedUser === 'all' && selectedAdminCampaign === 'all') {
            // Use the stats from main dashboard call and clear campaign options
            setAdminVoiceStats(stats?.systemListeningStatistics || null);
            setStats(prevStats => ({
                ...prevStats,
                availableVoiceCampaignsForAdmin: []
            }));
            return;
        }

        try {
            const params = {};
            if (selectedUser !== 'all') params.userId = selectedUser;
            if (selectedAdminCampaign !== 'all') params.campaignId = selectedAdminCampaign;

            const response = await campaignAPI.getAdminDashboardStats(params);
            setAdminVoiceStats(response.data.data.systemListeningStatistics);

            // Update stats with new campaign options when user changes
            if (selectedUser !== 'all' && response.data.data.availableVoiceCampaignsForAdmin) {
                setStats(prevStats => ({
                    ...prevStats,
                    availableVoiceCampaignsForAdmin: response.data.data.availableVoiceCampaignsForAdmin
                }));
            } else if (selectedUser === 'all') {
                setStats(prevStats => ({
                    ...prevStats,
                    availableVoiceCampaignsForAdmin: []
                }));
            }
        } catch (error) {
            toast.error('Failed to load admin voice statistics');
        }
    }, [selectedUser, selectedAdminCampaign, stats?.systemListeningStatistics]);

    const campaignTypeIcons = {
        sms: PhoneIcon,
        whatsapp: ChatBubbleLeftRightIcon,
        voice: SpeakerWaveIcon,
        SMS: PhoneIcon,
        Whatsapp: ChatBubbleLeftRightIcon,
        Voice: SpeakerWaveIcon,
    };

    const getInsightIcon = (type) => {
        switch (type) {
            case 'success': return ShieldCheckIcon;
            case 'warning': return ExclamationCircleIcon;
            case 'error': return ExclamationTriangleIcon;
            case 'info': return InformationCircleIcon;
            default: return LightBulbIcon;
        }
    };

    const getInsightColorClass = (type) => {
        switch (type) {
            case 'success': return 'bg-success-50 border-success-200 text-success-800';
            case 'warning': return 'bg-warning-50 border-warning-200 text-warning-800';
            case 'error': return 'bg-error-50 border-error-200 text-error-800';
            case 'info': return 'bg-primary-50 border-primary-200 text-primary-800';
            default: return 'bg-soft-50 border-soft-200 text-soft-800';
        }
    };

    const formatMonth = (yearMonth) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[yearMonth.month - 1]} ${yearMonth.year}`;
    };

    const formatUptime = (seconds) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${days}d ${hours}h ${minutes}m`;
    };

    // Get credit status based on API configuration instead of hardcoded values
    const getCreditStatus = (credits) => {
        if (!stats?.systemConfig?.creditThresholds) return 'normal';

        const { critical, veryLow, low } = stats.systemConfig.creditThresholds;

        if (credits === critical) return 'critical';
        if (credits <= veryLow) return 'veryLow';
        if (credits <= low) return 'low';
        return 'normal';
    };

    // Get credit status color classes based on API configuration
    const getCreditStatusColor = (credits) => {
        const status = getCreditStatus(credits);
        switch (status) {
            case 'critical': return 'bg-red-100 text-red-700';
            case 'veryLow': return 'bg-orange-100 text-orange-700';
            case 'low': return 'bg-yellow-100 text-yellow-700';
            default: return 'bg-blue-100 text-blue-700';
        }
    };

    // Get overall user status based on any low credit channel
    const getUserCreditStatus = (user) => {
        if (!stats?.systemConfig?.creditThresholds) return 'normal';

        const { critical, veryLow } = stats.systemConfig.creditThresholds;

        if (user.smsCredits === critical || user.whatsappCredits === critical || user.voiceCallCredits === critical) {
            return 'critical';
        }
        if (user.smsCredits <= veryLow || user.whatsappCredits <= veryLow || user.voiceCallCredits <= veryLow) {
            return 'veryLow';
        }
        return 'low';
    };

    // Get user status display text
    const getUserStatusText = (user) => {
        const status = getUserCreditStatus(user);
        switch (status) {
            case 'critical': return 'Critical';
            case 'veryLow': return 'Very Low';
            default: return 'Low';
        }
    };

    // Get user status color
    const getUserStatusColor = (user) => {
        const status = getUserCreditStatus(user);
        switch (status) {
            case 'critical': return 'bg-red-100 text-red-700';
            case 'veryLow': return 'bg-orange-100 text-orange-700';
            default: return 'bg-yellow-100 text-yellow-700';
        }
    };

    // Count users with specific credit status using API configuration
    const countUsersByCredits = (users, creditType, threshold) => {
        if (!stats?.systemConfig?.creditThresholds) return 0;

        const thresholdValue = stats.systemConfig.creditThresholds[threshold];

        return users.filter(user => {
            switch (creditType) {
                case 'sms': return user.smsCredits <= thresholdValue;
                case 'whatsapp': return user.whatsappCredits <= thresholdValue;
                case 'voice': return user.voiceCallCredits <= thresholdValue;
                case 'zero': return user.smsCredits === 0 || user.whatsappCredits === 0 || user.voiceCallCredits === 0;
                default: return false;
            }
        }).length;
    };

    // Custom Tooltip Component for better styling
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-4 rounded-lg shadow-lg border border-soft-200">
                    <p className="font-semibold text-soft-900 mb-2">{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between min-w-[180px] mb-1">
                            <span className="flex items-center">
                                <div
                                    className="w-3 h-3 rounded-full mr-2"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm text-soft-600">{entry.name}:</span>
                            </span>
                            <span className="font-medium text-soft-900">
                                {entry.value?.toLocaleString() || 0}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Low Credits Chart Component
    const LowCreditsChart = ({ data }) => {
        // Validate data
        if (!data || !Array.isArray(data) || data.length === 0) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 text-sm">No low credit users data available</p>
                    </div>
                </div>
            );
        }

        // Format data for the chart with safety checks
        const chartData = data.map(user => ({
            name: user.name && user.name.length > 15 ? user.name.substring(0, 15) + '...' : (user.name || 'Unknown'),
            fullName: user.name || 'Unknown',
            email: user.email || '',
            smsCredits: Number(user.smsCredits) || 0,
            whatsappCredits: Number(user.whatsappCredits) || 0,
            voiceCredits: Number(user.voiceCallCredits) || 0,
            totalCredits: Number(user.totalCredits) || 0
        }));

        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 60,
                    }}
                    barCategoryGap="20%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="rect"
                        formatter={(value) => (
                            <span className="text-sm font-medium text-soft-700">{value}</span>
                        )}
                    />
                    <Bar
                        dataKey="smsCredits"
                        name="SMS Credits"
                        fill="#dbeafe"
                        radius={[4, 4, 0, 0]}
                        stroke="#3b82f6"
                        strokeWidth={2}
                    />
                    <Bar
                        dataKey="whatsappCredits"
                        name="WhatsApp Credits"
                        fill="#d1fae5"
                        radius={[4, 4, 0, 0]}
                        stroke="#10b981"
                        strokeWidth={2}
                    />
                    <Bar
                        dataKey="voiceCredits"
                        name="Voice Credits"
                        fill="#fef3c7"
                        radius={[4, 4, 0, 0]}
                        stroke="#f59e0b"
                        strokeWidth={2}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    if (!isAdmin()) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="text-center">
                    <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600">You need admin privileges to access this dashboard.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Admin welcome section skeleton */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-8 border border-purple-200/50 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="h-8 bg-purple-200 rounded-lg w-72 animate-pulse mb-3"></div>
                            <div className="h-5 bg-purple-100 rounded-lg w-96 animate-pulse mb-6"></div>
                            <div className="flex items-center space-x-6">
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-purple-200/50">
                                    <div className="h-5 w-5 bg-purple-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-purple-200 rounded w-24 animate-pulse"></div>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-indigo-200/50">
                                    <div className="h-5 w-5 bg-indigo-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-indigo-200 rounded w-20 animate-pulse"></div>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-success-200/50">
                                    <div className="h-5 w-5 bg-success-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-success-200 rounded w-18 animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading center piece */}
                <div className="text-center py-12">
                    <div className="relative inline-flex items-center justify-center w-24 h-24 mb-6">
                        <div className="absolute inset-0 rounded-full border-4 border-purple-200 animate-spin border-t-purple-600"></div>
                        <div className="absolute inset-2 rounded-full border-2 border-indigo-100 animate-spin border-t-indigo-400" style={{ animationDirection: 'reverse', animationDuration: '2.5s' }}></div>
                        <div className="absolute inset-4 rounded-full border-2 border-purple-100 animate-pulse"></div>
                        <div className="absolute inset-6 rounded-full bg-gradient-to-r from-purple-200 to-indigo-200 animate-pulse"></div>
                        <ServerIcon className="h-8 w-8 text-purple-600 animate-pulse relative z-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-soft-900 mb-3">Loading System Dashboard</h3>
                    <p className="text-soft-600 text-lg mb-6">Gathering system metrics and performance data...</p>

                    {/* Advanced progress indicator */}
                    <div className="flex items-center justify-center space-x-2 mb-8">
                        <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce"></div>
                        <div className="w-3 h-3 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-3 h-3 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                        <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>

                    {/* Admin loading message */}
                    <div className="inline-flex items-center space-x-3 bg-white/80 px-8 py-4 rounded-full border border-purple-200/50 shadow-lg">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-300 border-t-purple-600"></div>
                        <span className="text-soft-600 font-semibold animate-pulse">Initializing system monitoring...</span>
                    </div>
                </div>

                {/* System health skeleton */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <div className="flex items-center mb-2">
                            <div className="h-6 w-6 bg-primary-200 rounded animate-pulse mr-2"></div>
                            <div className="h-6 bg-soft-200 rounded w-64 animate-pulse"></div>
                        </div>
                        <div className="h-4 bg-soft-100 rounded w-80 animate-pulse"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="text-center p-5 rounded-xl bg-primary-50 border border-primary-200/50">
                                <div className="h-8 bg-primary-200 rounded w-16 mx-auto animate-pulse mb-2"></div>
                                <div className="h-4 bg-primary-100 rounded w-24 mx-auto animate-pulse"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Campaign & User statistics skeleton */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {/* Campaign statistics skeleton */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                        <div className="mb-6">
                            <div className="flex items-center mb-2">
                                <div className="h-6 w-6 bg-primary-200 rounded animate-pulse mr-2"></div>
                                <div className="h-6 bg-soft-200 rounded w-48 animate-pulse"></div>
                            </div>
                            <div className="h-4 bg-soft-100 rounded w-64 animate-pulse"></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {[...Array(4)].map((_, index) => (
                                <div key={index} className="text-center p-4 rounded-xl bg-soft-50 border border-soft-200/50">
                                    <div className="h-6 bg-soft-200 rounded w-8 mx-auto animate-pulse mb-1"></div>
                                    <div className="h-4 bg-soft-100 rounded w-16 mx-auto animate-pulse"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* User statistics skeleton */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                        <div className="mb-6">
                            <div className="flex items-center mb-2">
                                <div className="h-6 w-6 bg-indigo-200 rounded animate-pulse mr-2"></div>
                                <div className="h-6 bg-soft-200 rounded w-40 animate-pulse"></div>
                            </div>
                            <div className="h-4 bg-soft-100 rounded w-56 animate-pulse"></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {[...Array(4)].map((_, index) => (
                                <div key={index} className="text-center p-4 rounded-xl bg-soft-50 border border-soft-200/50">
                                    <div className="h-6 bg-soft-200 rounded w-8 mx-auto animate-pulse mb-1"></div>
                                    <div className="h-4 bg-soft-100 rounded w-20 mx-auto animate-pulse"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* System performance metrics skeleton */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <div className="flex items-center mb-2">
                            <div className="h-6 w-6 bg-success-200 rounded animate-pulse mr-2"></div>
                            <div className="h-6 bg-soft-200 rounded w-56 animate-pulse"></div>
                        </div>
                        <div className="h-4 bg-soft-100 rounded w-72 animate-pulse"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="text-center p-5 rounded-xl bg-soft-50 border border-soft-200/50">
                                <div className="h-8 bg-soft-200 rounded w-16 mx-auto animate-pulse mb-2"></div>
                                <div className="h-4 bg-soft-100 rounded w-20 mx-auto animate-pulse"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Monthly trends skeleton */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <div className="h-6 bg-soft-200 rounded w-40 animate-pulse mb-2"></div>
                        <div className="h-4 bg-soft-100 rounded w-64 animate-pulse"></div>
                    </div>
                    <div className="h-80 bg-soft-50 rounded-lg animate-pulse"></div>
                </div>

                {/* Channel performance & low credit users skeleton */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {/* Channel performance skeleton */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                        <div className="mb-6">
                            <div className="h-6 bg-soft-200 rounded w-44 animate-pulse mb-2"></div>
                            <div className="h-4 bg-soft-100 rounded w-56 animate-pulse"></div>
                        </div>
                        <div className="h-64 bg-soft-50 rounded-lg animate-pulse"></div>
                    </div>

                    {/* Low credit users skeleton */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                        <div className="mb-6">
                            <div className="flex items-center mb-2">
                                <div className="h-6 w-6 bg-error-200 rounded animate-pulse mr-2"></div>
                                <div className="h-6 bg-soft-200 rounded w-36 animate-pulse"></div>
                            </div>
                            <div className="h-4 bg-soft-100 rounded w-48 animate-pulse"></div>
                        </div>
                        <div className="space-y-3">
                            {[...Array(4)].map((_, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-soft-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <div className="h-8 w-8 bg-soft-200 rounded-full animate-pulse"></div>
                                        <div>
                                            <div className="h-4 bg-soft-200 rounded w-24 animate-pulse mb-1"></div>
                                            <div className="h-3 bg-soft-100 rounded w-32 animate-pulse"></div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="h-4 bg-error-200 rounded w-12 animate-pulse mb-1"></div>
                                        <div className="h-3 bg-error-100 rounded w-16 animate-pulse"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* System insights skeleton */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <div className="flex items-center mb-2">
                            <div className="h-6 w-6 bg-secondary-200 rounded animate-pulse mr-2"></div>
                            <div className="h-6 bg-soft-200 rounded w-40 animate-pulse"></div>
                        </div>
                        <div className="h-4 bg-soft-100 rounded w-60 animate-pulse"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="p-4 rounded-lg bg-soft-50 border border-soft-200/50">
                                <div className="flex items-start space-x-3">
                                    <div className="h-8 w-8 bg-soft-200 rounded-lg animate-pulse mt-1"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-soft-200 rounded w-full animate-pulse mb-2"></div>
                                        <div className="h-3 bg-soft-100 rounded w-3/4 animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="text-center">
                    <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Admin Dashboard</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={fetchAdminDashboardStats}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Admin Welcome Section */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 sm:p-6 lg:p-8 border border-purple-200/50 shadow-sm">
                <div className="flex flex-col space-y-6">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-soft-900">
                            System Administration 🔧
                        </h1>
                        <p className="text-soft-700 text-base sm:text-lg leading-relaxed">
                            Monitor system performance, manage users, and analyze campaign effectiveness across the entire platform
                        </p>
                        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:flex-wrap lg:flex-row lg:items-center gap-3 sm:gap-4 lg:gap-6 text-soft-600">
                            <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-purple-200/50 flex-shrink-0">
                                <ServerIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 flex-shrink-0" />
                                <span className="font-medium text-xs sm:text-sm truncate">System Uptime: {formatUptime(stats?.systemHealth?.systemUptime || 0)}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-indigo-200/50 flex-shrink-0">
                                <UsersIcon className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 flex-shrink-0" />
                                <span className="font-medium text-xs sm:text-sm truncate">Total Users: {stats?.users?.total || 0}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-success-200/50 flex-shrink-0">
                                <TrophyIcon className="h-4 w-4 sm:h-5 sm:w-5 text-success-600 flex-shrink-0" />
                                <span className="font-medium text-xs sm:text-sm truncate">Success Rate: {stats?.performance?.successRate || 0}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* System Health Overview */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60">
                <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex items-center">
                        <CpuChipIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 mr-2 flex-shrink-0" />
                        <span className="truncate">System Health Overview</span>
                    </h3>
                    <p className="text-soft-600 text-sm sm:text-base">Real-time system performance and health indicators</p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-primary-50 border border-primary-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-primary-900 mb-1 sm:mb-2">
                            {stats?.systemHealth?.totalCampaigns?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-primary-600 font-medium">Total Campaigns</div>
                    </div>
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-success-50 border border-success-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-success-700 mb-1 sm:mb-2">
                            {stats?.systemHealth?.activeUserPercentage || 0}%
                        </div>
                        <div className="text-xs sm:text-sm text-success-600 font-medium">Active Users</div>
                    </div>
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-secondary-50 border border-secondary-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-secondary-700 mb-1 sm:mb-2">
                            {stats?.systemHealth?.totalContacts?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-secondary-600 font-medium">Total Contacts</div>
                    </div>
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-warning-50 border border-warning-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-warning-700 mb-1 sm:mb-2">
                            {stats?.systemHealth?.averageSuccessRate || 0}%
                        </div>
                        <div className="text-xs sm:text-sm text-warning-600 font-medium">Avg Success Rate</div>
                    </div>
                </div>
            </div>

            {/* Campaign & User Statistics */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                {/* Campaign Statistics */}
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-4 sm:mb-6">
                        <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 mr-2 flex-shrink-0" />
                            <span className="truncate">Campaign Statistics</span>
                        </h3>
                        <p className="text-soft-600 text-sm sm:text-base">System-wide campaign distribution and status</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-soft-50 border border-soft-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-soft-900 mb-1">{stats?.campaigns?.total || 0}</div>
                            <div className="text-xs sm:text-sm text-soft-600">Total</div>
                        </div>
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-success-50 border border-success-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-success-700 mb-1">{stats?.campaigns?.completed || 0}</div>
                            <div className="text-xs sm:text-sm text-success-600">Completed</div>
                        </div>
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-secondary-50 border border-secondary-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-secondary-700 mb-1">{stats?.campaigns?.ongoing || 0}</div>
                            <div className="text-xs sm:text-sm text-secondary-600">Ongoing</div>
                        </div>
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-warning-50 border border-warning-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-warning-700 mb-1">{stats?.campaigns?.pending || 0}</div>
                            <div className="text-xs sm:text-sm text-warning-600">Pending</div>
                        </div>
                    </div>
                </div>

                {/* User Statistics */}
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-4 sm:mb-6">
                        <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <UsersIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 mr-2 flex-shrink-0" />
                            <span className="truncate">User Statistics</span>
                        </h3>
                        <p className="text-soft-600 text-sm sm:text-base">Platform user distribution and activity</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-soft-50 border border-soft-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-soft-900 mb-1">{stats?.users?.total || 0}</div>
                            <div className="text-xs sm:text-sm text-soft-600">Total Users</div>
                        </div>
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-success-50 border border-success-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-success-700 mb-1">{stats?.users?.active || 0}</div>
                            <div className="text-xs sm:text-sm text-success-600">Active</div>
                        </div>
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-purple-50 border border-purple-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-purple-700 mb-1">{stats?.users?.admins || 0}</div>
                            <div className="text-xs sm:text-sm text-purple-600">Admins</div>
                        </div>
                        <div className="text-center p-3 sm:p-4 rounded-xl bg-blue-50 border border-blue-200/50">
                            <div className="text-lg sm:text-2xl font-bold text-blue-700 mb-1">{stats?.users?.regular || 0}</div>
                            <div className="text-xs sm:text-sm text-blue-600">Regular Users</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* System Performance Metrics */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60">
                <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex items-center">
                        <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-success-600 mr-2 flex-shrink-0" />
                        <span className="truncate">System Performance Metrics</span>
                    </h3>
                    <p className="text-soft-600 text-sm sm:text-base">Overall system performance across all users and campaigns</p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-soft-50 border border-soft-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-soft-900 mb-1 sm:mb-2">
                            {stats?.performance?.totalReach?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-soft-600 font-medium">Total Reach</div>
                    </div>
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-success-50 border border-success-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-success-700 mb-1 sm:mb-2">
                            {stats?.performance?.successRate || 0}%
                        </div>
                        <div className="text-xs sm:text-sm text-success-600 font-medium">Success Rate</div>
                    </div>
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-primary-50 border border-primary-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-primary-700 mb-1 sm:mb-2">
                            {stats?.performance?.deliveryRate || 0}%
                        </div>
                        <div className="text-xs sm:text-sm text-primary-600 font-medium">Delivery Rate</div>
                    </div>
                    <div className="text-center p-3 sm:p-5 rounded-xl bg-error-50 border border-error-200/50">
                        <div className="text-xl sm:text-3xl font-bold text-error-700 mb-1 sm:mb-2">
                            {stats?.performance?.failureRate || 0}%
                        </div>
                        <div className="text-xs sm:text-sm text-error-600 font-medium">Failure Rate</div>
                    </div>
                </div>
            </div>

            {/* System-wide Channel Performance */}
            {stats?.campaignTypes && stats.campaignTypes.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <ChartPieIcon className="h-6 w-6 text-primary-600 mr-2" />
                            System-wide Channel Performance
                        </h3>
                        <p className="text-soft-600">Performance comparison across all communication channels</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {stats.campaignTypes.map((channel, index) => {
                            const totalProcessed = channel.totalDelivered + channel.totalFailed + channel.totalSent;
                            const successRate = totalProcessed > 0 ? Math.round((channel.totalDelivered / totalProcessed) * 100) : 0;
                            const failureRate = totalProcessed > 0 ? Math.round((channel.totalFailed / totalProcessed) * 100) : 0;

                            const getChannelIcon = (type) => {
                                switch (type.toLowerCase()) {
                                    case 'sms': return PhoneIcon;
                                    case 'whatsapp': return ChatBubbleLeftRightIcon;
                                    case 'voice call': return SpeakerWaveIcon;
                                    default: return DocumentTextIcon;
                                }
                            };

                            const getChannelColor = (type) => {
                                switch (type.toLowerCase()) {
                                    case 'sms': return 'primary';
                                    case 'whatsapp': return 'success';
                                    case 'voice call': return 'secondary';
                                    default: return 'soft';
                                }
                            };

                            const color = getChannelColor(channel._id);
                            const Icon = getChannelIcon(channel._id);

                            return (
                                <div key={channel._id} className={`p-6 rounded-xl bg-${color}-50 border border-${color}-200/50 hover:shadow-md transition-all duration-200`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center space-x-3">
                                            <div className={`h-12 w-12 bg-${color}-100 rounded-xl flex items-center justify-center border border-${color}-200/50`}>
                                                <Icon className={`h-6 w-6 text-${color}-600`} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-soft-900">{channel._id.toUpperCase()}</h4>
                                                <p className="text-sm text-soft-600">{channel.count} campaigns</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`text-2xl font-bold text-${color}-700`}>{successRate}%</div>
                                            <div className="text-sm text-soft-600">Success Rate</div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-soft-600">Total Numbers:</span>
                                            <span className="font-semibold text-soft-900">{channel.totalNumbers?.toLocaleString() || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-success-600">Delivered:</span>
                                            <span className="font-semibold text-success-700">{channel.totalDelivered?.toLocaleString() || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-error-600">Failed:</span>
                                            <span className="font-semibold text-error-700">{channel.totalFailed?.toLocaleString() || 0}</span>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-soft-200">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-soft-600">Failure Rate:</span>
                                                <span className="font-semibold text-error-700">{failureRate}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Credit Utilization Analysis */}
            {stats?.creditUtilization && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 shadow-sm border border-green-200/50">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <CreditCardIcon className="h-6 w-6 text-green-600 mr-2" />
                            Credit Utilization Analysis
                        </h3>
                        <p className="text-soft-600">System-wide credit distribution and usage patterns</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-soft-900 mb-2">
                                {stats.creditUtilization.totalSmsCredits?.toLocaleString() || 0}
                            </div>
                            <div className="text-sm text-soft-600 font-medium mb-1">Total SMS Credits</div>
                            <div className="text-xs text-soft-500">
                                Avg: {Math.round(stats.creditUtilization.averageSmsCredits || 0)} per user
                            </div>
                        </div>
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-soft-900 mb-2">
                                {stats.creditUtilization.totalWhatsappCredits?.toLocaleString() || 0}
                            </div>
                            <div className="text-sm text-soft-600 font-medium mb-1">Total WhatsApp Credits</div>
                            <div className="text-xs text-soft-500">
                                Avg: {Math.round(stats.creditUtilization.averageWhatsappCredits || 0)} per user
                            </div>
                        </div>
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-soft-900 mb-2">
                                {stats.creditUtilization.totalVoiceCredits?.toLocaleString() || 0}
                            </div>
                            <div className="text-sm text-soft-600 font-medium mb-1">Total Voice Credits</div>
                            <div className="text-xs text-soft-500">
                                Avg: {Math.round(stats.creditUtilization.averageVoiceCredits || 0)} per user
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* System-wide Voice Campaign Listening Statistics */}
            {(stats?.systemListeningStatistics || adminVoiceStats) && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-purple-200/50">
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                                    <SpeakerWaveIcon className="h-6 w-6 text-purple-600 mr-2" />
                                    System-wide Voice Campaign Listening Statistics
                                    {adminVoiceStats?.userInfo && (
                                        <span className="ml-2 text-sm font-normal text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                            {adminVoiceStats.userInfo.name}
                                        </span>
                                    )}
                                    {adminVoiceStats?.campaignTitle && (
                                        <span className="ml-2 text-sm font-normal text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                                            {adminVoiceStats.campaignTitle}
                                        </span>
                                    )}
                                </h3>
                                <p className="text-soft-600">
                                    Platform-wide analysis of voice campaign engagement and listening patterns
                                    ({(adminVoiceStats || stats.systemListeningStatistics).totalVoiceCampaigns} campaigns • {(adminVoiceStats || stats.systemListeningStatistics).totalVoiceContacts?.toLocaleString()} contacts • {(adminVoiceStats || stats.systemListeningStatistics).totalUsersWithVoiceData} users)
                                </p>
                            </div>

                            {/* Filters */}
                            <div className="flex space-x-4">
                                {/* User Filter */}
                                {stats?.availableUsersWithVoice && stats.availableUsersWithVoice.length > 0 && (
                                    <div className="w-48">
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Filter by User:
                                        </label>
                                        <select
                                            value={selectedUser}
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                            className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white text-soft-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        >
                                            <option value="all">All Users</option>
                                            {stats.availableUsersWithVoice.map((user) => (
                                                <option key={user._id} value={user._id}>
                                                    {user.name} ({user.campaignCount} campaigns)
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Campaign Filter - Show only when user is selected */}
                                {selectedUser !== 'all' && stats?.availableVoiceCampaignsForAdmin && stats.availableVoiceCampaignsForAdmin.length > 0 && (
                                    <div className="w-48">
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Filter by Campaign:
                                        </label>
                                        <select
                                            value={selectedAdminCampaign}
                                            onChange={(e) => setSelectedAdminCampaign(e.target.value)}
                                            className="w-full px-3 py-2 border border-indigo-200 rounded-lg bg-white text-soft-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        >
                                            <option value="all">All User Campaigns</option>
                                            {stats.availableVoiceCampaignsForAdmin.map((campaign) => (
                                                <option key={campaign._id} value={campaign._id}>
                                                    {campaign.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* System Engagement Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-green-700 mb-2">
                                {(adminVoiceStats || stats.systemListeningStatistics).highEngagement}%
                            </div>
                            <div className="text-sm text-green-600 font-medium mb-1">High Engagement</div>
                            <div className="text-xs text-green-500">Pulse 1-4 sec across {adminVoiceStats?.userInfo ? 'user campaigns' : 'all users'}</div>
                        </div>
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-yellow-700 mb-2">
                                {(adminVoiceStats || stats.systemListeningStatistics).mediumEngagement}%
                            </div>
                            <div className="text-sm text-yellow-600 font-medium mb-1">Medium Engagement</div>
                            <div className="text-xs text-yellow-500">Pulse 5-6 sec across {adminVoiceStats?.userInfo ? 'user campaigns' : 'all users'}</div>
                        </div>
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-red-700 mb-2">
                                {(adminVoiceStats || stats.systemListeningStatistics).lowEngagement}%
                            </div>
                            <div className="text-sm text-red-600 font-medium mb-1">Low Engagement</div>
                            <div className="text-xs text-red-500">Pulse 7-10 sec</div>
                        </div>
                        <div className="text-center p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-3xl font-bold text-indigo-700 mb-2">
                                {(adminVoiceStats || stats.systemListeningStatistics).effectivenessScore}
                            </div>
                            <div className="text-sm text-indigo-600 font-medium mb-1">Effectiveness Score</div>
                            <div className="text-xs text-indigo-500">Weighted engagement rating</div>
                        </div>
                    </div>

                    {/* Detailed System Listening Breakdown */}
                    <div className="bg-white/50 rounded-xl p-6 border border-white/50">
                        <h4 className="text-lg font-semibold text-soft-900 mb-4">System-wide Listening Breakdown</h4>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                            <div className="text-center p-4 rounded-lg bg-gradient-to-b from-green-100 to-green-50 border border-green-200">
                                <div className="text-2xl font-bold text-green-700 mb-1">
                                    {(adminVoiceStats || stats.systemListeningStatistics).averageListeningStats.heard100Percent}%
                                </div>
                                <div className="text-xs text-green-600 font-medium">Pulse 1-2 sec</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-gradient-to-b from-blue-100 to-blue-50 border border-blue-200">
                                <div className="text-2xl font-bold text-blue-700 mb-1">
                                    {(adminVoiceStats || stats.systemListeningStatistics).averageListeningStats.heard80to99Percent}%
                                </div>
                                <div className="text-xs text-blue-600 font-medium">Pulse 3-4 sec</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-gradient-to-b from-yellow-100 to-yellow-50 border border-yellow-200">
                                <div className="text-2xl font-bold text-yellow-700 mb-1">
                                    {(adminVoiceStats || stats.systemListeningStatistics).averageListeningStats.heard50to79Percent}%
                                </div>
                                <div className="text-xs text-yellow-600 font-medium">Pulse 5-6 sec</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-gradient-to-b from-orange-100 to-orange-50 border border-orange-200">
                                <div className="text-2xl font-bold text-orange-700 mb-1">
                                    {(adminVoiceStats || stats.systemListeningStatistics).averageListeningStats.heard30to49Percent}%
                                </div>
                                <div className="text-xs text-orange-600 font-medium">Pulse 7-8 sec</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-gradient-to-b from-red-100 to-red-50 border border-red-200">
                                <div className="text-2xl font-bold text-red-700 mb-1">
                                    {(adminVoiceStats || stats.systemListeningStatistics).averageListeningStats.heard1to29Percent}%
                                </div>
                                <div className="text-xs text-red-600 font-medium">Pulse 9-10 sec</div>
                            </div>
                        </div>

                        {/* System Insights */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                                <div className="flex items-start space-x-3">
                                    <LightBulbIcon className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <h5 className="font-semibold text-indigo-900 mb-1">Platform Performance</h5>
                                        <p className="text-sm text-indigo-700">
                                            {(adminVoiceStats || stats.systemListeningStatistics).highEngagement >= 50
                                                ? `Excellent ${adminVoiceStats?.userInfo ? 'user' : 'platform'} performance! ${(adminVoiceStats || stats.systemListeningStatistics).highEngagement}% high engagement rate across ${adminVoiceStats?.userInfo ? 'their' : 'all'} voice campaigns.`
                                                : (adminVoiceStats || stats.systemListeningStatistics).highEngagement >= 30
                                                    ? `Good ${adminVoiceStats?.userInfo ? 'user' : 'platform'} engagement at ${(adminVoiceStats || stats.systemListeningStatistics).highEngagement}%. Room for improvement in voice campaign optimization.`
                                                    : `${adminVoiceStats?.userInfo ? 'User' : 'Platform'} needs attention: ${(adminVoiceStats || stats.systemListeningStatistics).highEngagement}% high engagement. Consider ${adminVoiceStats?.userInfo ? 'user training and' : ''} content optimization.`
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                                <div className="flex items-start space-x-3">
                                    <TrophyIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <h5 className="font-semibold text-green-900 mb-1">Effectiveness Insights</h5>
                                        <p className="text-sm text-green-700">
                                            Effectiveness score of {(adminVoiceStats || stats.systemListeningStatistics).effectivenessScore}/100 indicates
                                            {(adminVoiceStats || stats.systemListeningStatistics).effectivenessScore >= 70 ? ' excellent' :
                                                (adminVoiceStats || stats.systemListeningStatistics).effectivenessScore >= 50 ? ' good' : ' poor'}
                                            overall voice campaign performance{adminVoiceStats?.userInfo ? ` for ${adminVoiceStats.userInfo.name}` : ''}.
                                            {(adminVoiceStats || stats.systemListeningStatistics).lowEngagement > 40 &&
                                                ` High dropout rate needs attention.`
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Low Credits Users Chart */}
            {stats?.lowCreditUsers && stats.lowCreditUsers.length > 0 && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 shadow-sm border border-red-200/50">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <WarningIcon className="h-6 w-6 text-red-600 mr-2" />
                            Users with Low Credits (≤{stats?.systemConfig?.creditThresholds?.low || 100} in any channel)
                        </h3>
                        <p className="text-soft-600">Users who have low credits in SMS, WhatsApp, or Voice channels - immediate action required</p>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center p-4 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl font-bold text-blue-700 mb-1">
                                {countUsersByCredits(stats.lowCreditUsers, 'sms', 'low')}
                            </div>
                            <div className="text-sm text-blue-600 font-medium">Low SMS Credits</div>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl font-bold text-green-700 mb-1">
                                {countUsersByCredits(stats.lowCreditUsers, 'whatsapp', 'low')}
                            </div>
                            <div className="text-sm text-green-600 font-medium">Low WhatsApp Credits</div>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl font-bold text-yellow-700 mb-1">
                                {countUsersByCredits(stats.lowCreditUsers, 'voice', 'low')}
                            </div>
                            <div className="text-sm text-yellow-600 font-medium">Low Voice Credits</div>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl font-bold text-red-700 mb-1">
                                {countUsersByCredits(stats.lowCreditUsers, 'zero', 'critical')}
                            </div>
                            <div className="text-sm text-red-600 font-medium">Zero in Any Channel</div>
                        </div>
                    </div>

                    {/* Chart */}
                    <div className="h-80 bg-white/50 rounded-xl p-4">
                        {stats.lowCreditUsers && stats.lowCreditUsers.length > 0 ? (
                            <LowCreditsChart data={stats.lowCreditUsers} />
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 text-sm">No users with low credits found</p>
                                    <p className="text-gray-400 text-xs mt-1">All users have sufficient credits</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quick Action Table */}
                    <div className="mt-6 bg-white/70 rounded-xl p-4 border border-white/50">
                        <h4 className="text-lg font-bold text-soft-900 mb-4 flex items-center">
                            <CreditCardIcon className="h-5 w-5 text-red-600 mr-2" />
                            Quick Actions Required
                        </h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left border-b border-soft-200">
                                        <th className="pb-2 font-semibold text-soft-700">User</th>
                                        <th className="pb-2 font-semibold text-soft-700">SMS</th>
                                        <th className="pb-2 font-semibold text-soft-700">WhatsApp</th>
                                        <th className="pb-2 font-semibold text-soft-700">Voice</th>
                                        <th className="pb-2 font-semibold text-soft-700">Total</th>
                                        <th className="pb-2 font-semibold text-soft-700">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.lowCreditUsers.slice(0, 5).map((user, index) => (
                                        <tr key={user._id} className="border-b border-soft-100">
                                            <td className="py-3">
                                                <div>
                                                    <p className="font-medium text-soft-900">{user.name}</p>
                                                    <p className="text-xs text-soft-500">{user.email}</p>
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${getCreditStatusColor(user.smsCredits)}`}>
                                                    {user.smsCredits}
                                                </span>
                                            </td>
                                            <td className="py-3">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${getCreditStatusColor(user.whatsappCredits)}`}>
                                                    {user.whatsappCredits}
                                                </span>
                                            </td>
                                            <td className="py-3">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${getCreditStatusColor(user.voiceCallCredits)}`}>
                                                    {user.voiceCallCredits}
                                                </span>
                                            </td>
                                            <td className="py-3">
                                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${getCreditStatusColor(user.totalCredits)}`}>
                                                    {user.totalCredits}
                                                </span>
                                            </td>
                                            <td className="py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUserStatusColor(user)}`}>
                                                    {getUserStatusText(user)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 text-center">
                            <Link
                                to="/users"
                                className="inline-flex items-center text-red-600 hover:text-red-700 font-medium text-sm bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors duration-200 border border-red-200/50"
                            >
                                <CreditCardIcon className="h-4 w-4 mr-1" />
                                Manage User Credits
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* System Insights */}
            {stats?.systemInsights && stats.systemInsights.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <SparklesIcon className="h-6 w-6 text-secondary-600 mr-2" />
                            System Intelligence Insights
                        </h3>
                        <p className="text-soft-600">AI-powered system analysis and recommendations</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {stats.systemInsights.map((insight, index) => {
                            const Icon = getInsightIcon(insight.type);
                            return (
                                <div
                                    key={index}
                                    className={`p-4 rounded-xl border-2 ${getInsightColorClass(insight.type)}`}
                                >
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-lg mb-1">{insight.title}</h4>
                                            <p className="text-sm opacity-90">{insight.message}</p>
                                        </div>
                                        {insight.metric && (
                                            <div className="flex-shrink-0 text-right">
                                                <div className="text-2xl font-bold">{insight.metric}{insight.type === 'info' && insight.title.includes('Channel') ? '' : '%'}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Top Performing Users */}
                {stats?.topUsers && stats.topUsers.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                                <TrophyIcon className="h-6 w-6 text-yellow-600 mr-2" />
                                Top Performing Users
                            </h3>
                            <p className="text-soft-600">Users with highest campaign success rates</p>
                        </div>
                        <div className="space-y-4">
                            {stats.topUsers.slice(0, 5).map((user, index) => (
                                <div key={user._id} className="flex items-center justify-between p-4 rounded-xl bg-soft-50 hover:bg-soft-100 transition-colors border border-soft-200/50">
                                    <div className="flex items-center space-x-4">
                                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-yellow-500' :
                                            index === 1 ? 'bg-gray-400' :
                                                index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                                            }`}>
                                            {index + 1}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-soft-900">{user.userInfo.name}</p>
                                            <p className="text-sm text-soft-600">{user.campaignCount} campaigns</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-success-700">{user.successRate}%</div>
                                        <div className="text-sm text-soft-600">{user.totalDelivered?.toLocaleString() || 0} delivered</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recent System Activity */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                                <ClipboardDocumentListIcon className="h-6 w-6 text-indigo-600 mr-2" />
                                Recent System Activity
                            </h3>
                            <p className="text-soft-600">Latest campaigns across all users</p>
                        </div>
                        <Link
                            to="/campaigns"
                            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm bg-primary-50 hover:bg-primary-100 px-4 py-2 rounded-lg transition-colors duration-200 border border-primary-200/50"
                        >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View All
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {stats?.recentCampaigns?.length > 0 ? (
                            stats.recentCampaigns.slice(0, 5).map((campaign) => (
                                <div key={campaign._id} className="flex items-center justify-between p-4 rounded-xl bg-soft-50 hover:bg-soft-100 transition-colors border border-soft-200/50">
                                    <div className="flex items-center space-x-4">
                                        <div className="h-12 w-12 bg-soft-200 rounded-xl flex items-center justify-center border border-soft-300/50">
                                            {React.createElement(campaignTypeIcons[campaign.type] || DocumentTextIcon, {
                                                className: "h-6 w-6 text-soft-600"
                                            })}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-soft-900">{campaign.title}</p>
                                            <p className="text-sm text-soft-600">
                                                by {campaign.createdBy.name} • {campaign.statistics?.totalNumbers || 0} numbers
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${campaign.status === 'Completed' ? 'bg-success-100 text-success-700 border border-success-200/50' :
                                            campaign.status === 'Ongoing' ? 'bg-secondary-100 text-secondary-700 border border-secondary-200/50' :
                                                campaign.status === 'Pending' ? 'bg-warning-100 text-warning-700 border border-warning-200/50' :
                                                    'bg-error-100 text-error-700 border border-error-200/50'
                                            }`}>
                                            {campaign.status}
                                        </span>
                                        <p className="text-xs text-soft-500 mt-1">
                                            {new Date(campaign.createdAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <ClipboardDocumentListIcon className="h-12 w-12 text-soft-400 mx-auto mb-3" />
                                <p className="text-soft-600">No recent campaigns</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Monthly System Trends */}
            {stats?.monthlySystemTrends && stats.monthlySystemTrends.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <ArrowTrendingUpIcon className="h-6 w-6 text-success-600 mr-2" />
                            System Performance Trends
                        </h3>
                        <p className="text-soft-600">12-month system-wide performance and activity trends</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {stats.monthlySystemTrends.slice(-6).map((trend, index) => (
                            <div key={index} className="p-4 rounded-xl bg-soft-50 border border-soft-200/50">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-soft-900">{formatMonth(trend._id)}</h4>
                                    <div className="text-right">
                                        <div className="text-sm font-bold text-success-700">{trend.successRate}%</div>
                                        <div className="text-xs text-soft-600">Success</div>
                                    </div>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-soft-600">Campaigns:</span>
                                        <span className="font-medium text-soft-900">{trend.campaignCount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-soft-600">Active Users:</span>
                                        <span className="font-medium text-soft-900">{trend.activeUsers}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-success-600">Delivered:</span>
                                        <span className="font-medium text-success-700">{trend.totalDelivered?.toLocaleString() || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard; 